import { Loader2, X } from "lucide-react";
import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Banner } from "@/components/ui/banner";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useApproveLegacyCert } from "@/hooks/queries/useApproveLegacyCert";
import { useRejectLegacyCert } from "@/hooks/queries/useRejectLegacyCert";
import { useLegacyCertDownloadUrl } from "@/hooks/queries/useLegacyCertDownloadUrl";
import { PENDING_CERTS_QUERY_KEY } from "@/hooks/queries/useListPendingLegacyCerts";
import type { PendingLegacyCertItem } from "@/hooks/queries/admin-legacy-cert-types";
import { formatDate } from "@/lib/format-date";
import {
  isStaleCertError,
  toLegacyCertAdminErrorMessage,
} from "@/routes/admin/legacy-cert-admin-error";

// Review panel (UX §13.10) — a modal that renders the uploaded certificate (inline
// PDF / image via a short-TTL pre-signed GET) plus the clinician's OPERATIONAL
// metadata only (name/email/org — no PHI, R-E16-5) and the two decision CTAs.
//
// Story 16.4 wired Approve; Story 16.5 wires Reject. Reject is a high-consequence,
// low-frequency action, so it uses a two-step reason-capture flow (Decision 5):
// clicking "Reject" reveals a required reason <textarea> + a "Confirm rejection"
// destructive submit (disabled until the reason is non-empty), while the Approve CTA
// is hidden to prevent a mis-click. The reason is surfaced verbatim to the clinician
// on next sign-in (already wired on the clinician card, 16.3). Reject is wholly
// in-context — it does NOT enable the account and emits no domain event.
//
// The modal is the shadcn Dialog primitive (Radix) — focus trap, focus restore,
// scroll-lock, aria-modal, and Escape come for free (CLAUDE.md "shadcn/ui primitives
// first"; WCAG dialog pattern).

interface LegacyCertReviewPanelProps {
  cert: PendingLegacyCertItem;
  /** Close without a decision (backdrop / Escape / Cancel). */
  onClose: () => void;
  /** Called after a successful decision (approve OR reject) — the parent closes and
   *  the queue refetches (each hook self-invalidates the pending-certs query). */
  onDecided: () => void;
}

function CertPreview({ certId }: { certId: string }) {
  const { data, isPending, isError } = useLegacyCertDownloadUrl(certId);

  if (isPending) {
    return (
      <div
        className="flex h-72 items-center justify-center rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] text-sm text-[color:var(--text-tertiary)]"
        role="status"
        aria-busy="true"
      >
        Loading certificate…
      </div>
    );
  }

  if (isError || !data || !data.download_url.startsWith("https://")) {
    return (
      <Banner variant="danger" title="Couldn't load the certificate">
        The certificate preview is temporarily unavailable. You can still record a
        decision, or try reopening this certificate.
      </Banner>
    );
  }

  const isImage = (data.mime_type ?? "").startsWith("image/");
  if (isImage) {
    return (
      <img
        src={data.download_url}
        alt="Uploaded certificate"
        className="max-h-[60vh] w-full rounded-2xl border border-[color:var(--border-primary)] object-contain"
      />
    );
  }
  // PDF (and any non-image) — inline iframe; the pre-signed URL has no
  // Content-Disposition, so the browser renders it in place.
  //
  // gh-427: PDF open-params hide the viewer nav-pane + fit the page to width. They live
  // in the URL FRAGMENT — stripped before the S3 GET, so the SigV4 signature is intact.
  // Chromium/Acrobat only (Firefox PDF.js ignores them) — the new-tab link is the fallback.
  const pdfPreviewUrl = `${data.download_url}#view=FitH&navpanes=0&toolbar=1`;
  return (
    <div className="flex flex-col gap-2">
      <iframe
        src={pdfPreviewUrl}
        title="Uploaded certificate"
        className="h-[60vh] w-full rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)]"
      />
      <a
        href={data.download_url}
        target="_blank"
        rel="noopener noreferrer"
        className="self-end text-sm font-semibold text-[color:var(--text-brand-secondary)] underline-offset-2 hover:underline"
      >
        Open the certificate in a new tab
      </a>
    </div>
  );
}

export function LegacyCertReviewPanel({
  cert,
  onClose,
  onDecided,
}: LegacyCertReviewPanelProps) {
  const approve = useApproveLegacyCert();
  const reject = useRejectLegacyCert();
  const queryClient = useQueryClient();
  const [error, setError] = React.useState<string | null>(null);
  // "view" shows Approve + Reject; "rejecting" reveals the reason capture + confirm.
  const [mode, setMode] = React.useState<"view" | "rejecting">("view");
  const [reason, setReason] = React.useState("");
  const rejectReasonId = React.useId();
  const rejectErrorId = React.useId();
  const reasonRef = React.useRef<HTMLTextAreaElement>(null);

  const busy = approve.isPending || reject.isPending;
  const reasonValid = reason.trim().length > 0;

  // Move focus into the reason field when reject-capture opens (low-error UX).
  React.useEffect(() => {
    if (mode === "rejecting") reasonRef.current?.focus();
  }, [mode]);

  // A concurrent decision (another super_admin already decided this cert) leaves the
  // queue stale even though THIS decision didn't land — refetch behind the banner.
  const handleDecisionError = (err: unknown) => {
    setError(toLegacyCertAdminErrorMessage(err));
    if (isStaleCertError(err)) {
      void queryClient.invalidateQueries({ queryKey: PENDING_CERTS_QUERY_KEY });
    }
  };

  const onApprove = () => {
    if (busy) return;
    setError(null);
    approve.mutate(cert.id, {
      onSuccess: () => onDecided(),
      onError: handleDecisionError,
    });
  };

  const startReject = () => {
    setError(null);
    setReason(""); // entry owns its invariant — start from a clean reason field
    setMode("rejecting");
  };

  const cancelReject = () => {
    if (reject.isPending) return;
    setMode("view");
    setReason("");
    setError(null);
  };

  const onConfirmReject = () => {
    if (busy) return;
    const trimmed = reason.trim();
    if (!trimmed) return; // server-side LEGACY-400 is the defense; this is the guard.
    setError(null);
    reject.mutate(
      { certId: cert.id, reason: trimmed },
      {
        onSuccess: () => onDecided(),
        onError: handleDecisionError,
      },
    );
  };

  const isRejecting = mode === "rejecting";

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        // Radix routes Escape / overlay-click / the header close through here. Block
        // dismissal while a decision is in flight.
        if (!next && !busy) onClose();
      }}
    >
      {/* gh-427: consumer-local width override — shared Dialog 720px default unchanged. */}
      <DialogContent className="w-[min(1100px,95vw)]">
        <DialogHeader className="flex-row items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <DialogTitle>Review certificate</DialogTitle>
            <DialogDescription>
              {isRejecting
                ? "Enter a reason for rejecting this certificate. The clinician sees it on next sign-in."
                : "Verify the uploaded AMPS certificate before approving."}
            </DialogDescription>
          </div>
          <DialogClose asChild>
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              aria-label="Close review"
            >
              <X aria-hidden="true" className="h-4 w-4" />
            </Button>
          </DialogClose>
        </DialogHeader>

        {/* Operational metadata only — no PHI (R-E16-5). */}
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="font-semibold text-[color:var(--text-secondary)]">Clinician</dt>
          <dd className="text-[color:var(--text-primary)]">{cert.clinician_name ?? "—"}</dd>
          <dt className="font-semibold text-[color:var(--text-secondary)]">Email</dt>
          <dd className="text-[color:var(--text-primary)]">{cert.email}</dd>
          <dt className="font-semibold text-[color:var(--text-secondary)]">Organization</dt>
          <dd className="text-[color:var(--text-primary)]">{cert.organization ?? "—"}</dd>
          <dt className="font-semibold text-[color:var(--text-secondary)]">Uploaded</dt>
          <dd className="text-[color:var(--text-primary)] [font-variant-numeric:tabular-nums]">
            {formatDate(cert.uploaded_at)}
          </dd>
        </dl>

        <CertPreview certId={cert.id} />

        {isRejecting ? (
          <div className="flex flex-col gap-2">
            <Label htmlFor={rejectReasonId}>Reason for rejection</Label>
            <Textarea
              ref={reasonRef}
              id={rejectReasonId}
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={1000}
              aria-required
              aria-describedby={error ? rejectErrorId : undefined}
              disabled={reject.isPending}
              placeholder="e.g. The uploaded scan is illegible — please upload a clearer copy."
            />
          </div>
        ) : null}

        {error ? (
          <Banner
            variant="danger"
            id={isRejecting ? rejectErrorId : undefined}
            title={
              isRejecting
                ? "Couldn't reject the certificate"
                : "Couldn't approve the certificate"
            }
          >
            {error}
          </Banner>
        ) : null}

        <DialogFooter>
          {isRejecting ? (
            <>
              <Button variant="secondary" onClick={cancelReject} disabled={reject.isPending}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={onConfirmReject}
                disabled={!reasonValid || reject.isPending}
              >
                {reject.isPending ? (
                  <>
                    <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
                    Rejecting…
                  </>
                ) : (
                  "Confirm rejection"
                )}
              </Button>
            </>
          ) : (
            <>
              <Button variant="destructive" onClick={startReject} disabled={busy}>
                Reject
              </Button>
              <Button variant="primary" onClick={onApprove} disabled={busy}>
                {approve.isPending ? (
                  <>
                    <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
                    Approving…
                  </>
                ) : (
                  "Approve"
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
